import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { drw, errText } from "./api";

/** TKO-3 §2 — ASI / field-change marks rolled up for the Change Orders tab. A PM attaches them to
 *  an existing CO or starts a draft pre-filled with the items; the app never submits. Hidden
 *  unless Settings › "Surface change items in Change Orders" is on. */
export default function PendingChangeItems({ projectId, changeOrders = [], onChanged }) {
  const [data, setData] = useState(null);
  const [sel, setSel] = useState(new Set());
  const [co, setCo] = useState("");
  const load = () => drw.get(`/projects/${projectId}/takeoff/change-items`).then(({ data: d }) => setData(d)).catch(() => setData(null));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId]);
  if (!data?.enabled || !data.items.length) return null;
  const ids = data.items.filter((_, i) => sel.has(i)).flatMap((r) => r.mark_ids);
  const attach = async () => {
    try {
      const { data: out } = await drw.post(`/projects/${projectId}/takeoff/change-items/attach`, { mark_ids: ids, co_id: co });
      toast.success(co ? `${out.lines} line(s) added to the CO` : `CO draft started with ${out.lines} line(s)`);
      setSel(new Set()); load(); onChanged?.();
    } catch (e) { toast.error(errText(e)); }
  };
  return (
    <div className="rounded-xl border border-sky-200 bg-sky-50 p-3 mb-3" data-testid="pending-change-items">
      <div className="flex items-center gap-2 flex-wrap text-sm">
        <span className="font-semibold text-sky-900">Pending change items from the plan</span>
        <span className="text-xs text-sky-800">{data.items.length} group(s)</span>
        <select value={co} onChange={(e) => setCo(e.target.value)} className="ml-auto h-8 border rounded-md px-2 text-xs bg-white" data-testid="change-items-co">
          <option value="">Start a CO draft…</option>
          {changeOrders.filter((c) => c.status === "draft").map((c) => <option key={c.id} value={c.id}>{c.number} · {c.title}</option>)}
        </select>
        <button type="button" onClick={attach} disabled={!ids.length} className="h-8 px-3 rounded-md bg-neutral-900 text-white text-xs disabled:opacity-50" data-testid="change-items-attach">{co ? "Attach" : "Draft CO"} ({ids.length})</button>
      </div>
      <table className="w-full text-xs mt-2">
        <thead><tr className="text-left text-neutral-500"><th /><th>Symbol</th><th>Scope</th><th className="text-right">Count</th><th>Sheet</th><th>Reason</th><th>Who / when</th><th>Note</th></tr></thead>
        <tbody>
          {data.items.map((r, i) => (
            <tr key={i} className="border-t" data-testid={`change-item-${i}`}>
              <td><input type="checkbox" checked={sel.has(i)} onChange={(e) => setSel((s) => { const n = new Set(s); e.target.checked ? n.add(i) : n.delete(i); return n; })} /></td>
              <td className="py-1 font-semibold">{r.label}</td><td>{r.scope_code} · {r.product_code}</td><td className="text-right">{r.count}</td>
              <td className="font-mono">{r.sheet_number}</td><td>{r.reason}</td><td className="text-neutral-500">{r.who} · {String(r.when).slice(0, 10)}</td><td className="truncate max-w-[12rem]">{r.note}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
